/**
 * BLOCK 102 Item 1 — SINGLE shared source of truth for what formatting a
 * legal page may contain.
 *
 * Both sides derive from here, so a mismatch between the WYSIWYG editor and
 * the public renderer's sanitizer is architecturally impossible:
 *   - the Quill editor's allowed `formats` = LEGAL_QUILL_FORMATS
 *   - the public DOMPurify whitelist's ALLOWED_TAGS is DERIVED from that same
 *     list via QUILL_FORMAT_TO_TAGS (plus fixed structural tags)
 *
 * To add a new format you edit LEGAL_QUILL_FORMATS + its tag mapping in ONE
 * place; the sanitizer expands automatically. A format added without a tag
 * mapping produces no tags, so the sanitizer strips it cleanly on render
 * (fail-closed) rather than silently reintroducing an unrenderable-content
 * crash class.
 */
import DOMPurify from "dompurify";

// The one canonical list of Quill formats a legal page supports.
export const LEGAL_QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "list", "link", "blockquote",
];

// Each supported format -> the HTML tags Quill can emit for it.
const QUILL_FORMAT_TO_TAGS = {
  header: ["h1", "h2", "h3"],
  bold: ["strong", "b"],
  italic: ["em", "i"],
  underline: ["u"],
  list: ["ul", "ol", "li"],
  link: ["a"],
  blockquote: ["blockquote"],
};

// Structural tags that are always present regardless of formatting choices.
const BASE_STRUCTURAL_TAGS = ["p", "br", "span"];

// DERIVED sanitizer whitelist — never hand-maintained.
export const LEGAL_ALLOWED_TAGS = Array.from(
  new Set([
    ...BASE_STRUCTURAL_TAGS,
    ...LEGAL_QUILL_FORMATS.flatMap((f) => QUILL_FORMAT_TO_TAGS[f] || []),
  ])
);

export const LEGAL_ALLOWED_ATTR = ["href", "target", "rel", "class"];

// Editor toolbar, kept alongside the format list it drives.
export const LEGAL_QUILL_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "blockquote"],
  ["clean"],
];

/** Sanitize DB-stored legal HTML for the public renderer using the derived whitelist. */
export function sanitizeLegalHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: LEGAL_ALLOWED_TAGS,
    ALLOWED_ATTR: LEGAL_ALLOWED_ATTR,
  });
}
